"use client";

import React from "react";
import { ShieldCheck, FileCheck, Landmark, HardHat, Wrench, Zap, Info } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const standards = [{
  name: "Neural Core",
  description: "Advanced processing architecture designed for complex support case resolution and logic.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/logo_neural_core_tech.png",
  icon: Zap
}, {
  name: "ISO 27001",
  description: "Global standards for information security management systems ensuring data integrity.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/logo_iso_security_standard.png",
  icon: ShieldCheck
}, {
  name: "Cloud Native",
  description: "Seamlessly integrated with major cloud providers for 99.9% uptime and scalability.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/logo_cloud_infrastructure.png",
  icon: Landmark
}, {
  name: "API Verified",
  description: "Standardized protocols for effortless integration with your existing IT ecosystem.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/logo_api_integration_verified.png",
  icon: FileCheck
}, {
  name: "Logic Mesh",
  description: "Proprietary algorithm network that optimizes support workflows and reduces costs.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/logo_logic_mesh_network.png",
  icon: Wrench
}, {
  name: "Auto-Scale",
  description: "Dynamic resource allocation that adapts to your support volume in real-time.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686883/img/logo_autoscale_efficiency.png",
  icon: HardHat
}];
export default function TrustedByInnovators() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.5
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="223739" id="trusted-by-innovators" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: 'linear-gradient(var(--color-primary) 1px, transparent 1px), linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }}></div>
      
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerReveal.ref} className={`text-center mb-20 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-default font-bold tracking-widest uppercase text-sm md:text-base lg:text-lg text-muted-foreground">
            Powering the World's <span className="text-foreground">Most Efficient</span> Teams
          </h2>
          <div className="w-24 h-px bg-primary mx-auto mt-8 opacity-30"></div>
        </div>

        <div ref={gridReveal.ref} className={`grid grid-cols-2 md:grid-cols-3 gap-12 md:gap-20 items-center justify-items-center max-w-5xl mx-auto transition-all duration-1000 delay-300 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {standards.map((standard, i) => <div key={standard.name} data-index={i} className="flex justify-center w-full">
              <HoverCard openDelay={100} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="group relative cursor-pointer p-6 flex items-center justify-center w-full aspect-[3/2]">
                    {}
                    <img src={standard.logo} alt={`${standard.name} Logo`} className="max-h-16 md:max-h-20 w-auto object-contain transition-all duration-700 ease-in-out grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 group-hover:drop-shadow-[0_0_15px_rgba(185,100,50,0.4)] group-hover:scale-105" />
                    {}
                    <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/0 group-hover:border-primary/40 transition-colors duration-500"></div>
                    <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/0 group-hover:border-primary/40 transition-colors duration-500"></div>
                    <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/0 group-hover:border-primary/40 transition-colors duration-500"></div>
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/0 group-hover:border-primary/40 transition-colors duration-500"></div>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="center" className="w-72 bg-card/80 backdrop-blur-md border-border/40 shadow-2xl p-5 rounded-lg">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 border-b border-border/20 pb-3">
                      <div className="p-2 bg-primary/10 rounded-md text-primary">
                        <standard.icon size={18} strokeWidth={1.5} className="drop-shadow-[0_0_8px_rgba(185,100,50,0.5)]" />
                      </div>
                      <h4 className="font-mono font-bold text-foreground tracking-tight text-xs uppercase">
                        {standard.name}
                      </h4>
                    </div>
                    <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                      {standard.description}
                    </p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}